import { SHOW_JSD_LABEL, SHOW_MATCH_PERCENTAGE } from "@/config/displayPolicy";
import type { FeatureData } from "@/types/spectrum";
import { formatRt } from "@/utils/formatRt";
import { countObservations } from "@/utils/observationGrouping";
import { panelCls } from "@/utils/panelCls";

interface AIAnalysisSummaryProps {
  selectedFeature: string | null;
  features: FeatureData[];
  /** 분석 행의 실제 상태. 없으면 상태 블록을 내지 않는다(모르는 것은 말하지 않는다). */
  analysisStatus?: string;
  /** MS 원본 결측(D19) — 분석은 끝났지만 화합물 동정은 수행되지 않았다. */
  isMsMissing?: boolean;
}

/**
 * 분석 상태 문구 — 이 패널 전용.
 *
 * `analysisStatusLabels`(대기/실행중/완료/실패)는 목록·표의 짧은 배지용이라
 * 문장으로 읽히지 않는다. 여기서는 "AI 분석 …" 형태의 완성된 문구를 쓴다.
 * 맵에 없는 값은 지어내지 않고 원값을 그대로 둔다(SampleInfo 와 같은 원칙).
 */
const STATUS_TEXT: Record<string, string> = {
  pending: "분석 대기 중",
  running: "AI 분석 진행 중",
  completed: "AI 분석 완료",
  failed: "AI 분석 실패",
};

/** 상태 점 — 진행 중인 것만 깜박인다(완료가 깜박이면 아직 도는 것으로 읽힌다). */
const STATUS_DOT: Record<string, string> = {
  pending: "bg-gray-400",
  running: "bg-yellow-500 animate-pulse",
  completed: "bg-green-500",
  failed: "bg-red-500",
};

/**
 * AIAnalysisSummary — 오른쪽 스티키 "시료 수준 보조" 패널.
 *
 * 역할 분리: 시료 RoMSH 결론은 상단 ConclusionStripV2, 선택 물질 상세는
 * 중앙 FeatureDetailPanel이 전담. 이 패널은 중복을 피하고 시료 수준 요약만 담는다.
 *
 * 표시 데이터는 전부 backend API 실측/동정 결과(features)에서 파생한다.
 * (가속제 ASTM 카테고리 분류는 backend에 카테고리 메타가 없어 폐기 — 실데이터 원칙.)
 */
const AIAnalysisSummary = ({
  selectedFeature,
  features,
  analysisStatus,
  isMsMissing = false,
}: AIAnalysisSummaryProps) => {
  const feature = selectedFeature
    ? features.find((item) => item.feature_id === selectedFeature)
    : null;

  // T1: 관찰(스캔) 수와 후보 수를 구분 — 같은 스캔 후보 여러 개는 관찰 1건
  const { observationCount, candidateCount } = countObservations(features);
  // 2026-08-31: 같은 물질이 여러 스캔에 기록되므로 종 수는 따로 센다
  const distinctCompoundCount = new Set(
    features.map((f) => f.predicted_substance),
  ).size;

  // 동정 신뢰도 분포 — JSD 구간별 카운트 (FeatureDetailPanel getJsdLabel과 동일 기준).
  // jsd_distance가 있는 feature만 집계 (실측 MS 매칭 결과).
  const jsdScored = features.filter((f) => f.jsd_distance != null);
  const jsdHigh = jsdScored.filter(
    (f) => (f.jsd_distance as number) < 0.2,
  ).length;
  const jsdMid = jsdScored.filter(
    (f) =>
      (f.jsd_distance as number) >= 0.2 && (f.jsd_distance as number) < 0.5,
  ).length;
  const jsdLow = jsdScored.filter(
    (f) => (f.jsd_distance as number) >= 0.5,
  ).length;

  return (
    <div className={panelCls("standard", "h-full")}>
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
        <span className="w-2 h-2 bg-red-500 rounded-full mr-2" />
        AI 분석 요약
      </h3>

      <div className="space-y-4">
        {feature && (
          <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-3">
            <div className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-1">
              선택 물질 컨텍스트
            </div>
            <div className="text-base font-semibold text-blue-950">
              {feature.predicted_substance}
            </div>
            <div className="mt-1 text-sm text-blue-900">
              RT {formatRt(feature.rt)} min / m/z {feature.top_mz}
              {SHOW_MATCH_PERCENTAGE && ` / 매칭 ${feature.matching_score}%`}
            </div>
            <p className="mt-2 text-xs text-blue-700">
              상세 정보는 좌측 상세 패널 참조
            </p>
          </div>
        )}

        {/* 후보 요약 — 전부 backend 실 결과 기반. T1: 관찰 수 ≠ 후보 수 */}
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            후보 요약
          </div>
          <div className="flex items-baseline justify-between">
            <span
              className="text-sm text-gray-700"
              title="관찰 = AI 후보가 기록된 MS 스캔 1개. 같은 스캔의 후보 여러 개는 한 관찰로 셉니다. 같은 물질이 여러 스캔에서 나오면 각 스캔에 기록됩니다."
            >
              관찰(스캔)
            </span>
            <span
              data-testid="observation-count"
              className="text-base font-bold text-gray-900"
            >
              {observationCount}
              <span className="ml-0.5 text-xs font-normal text-gray-500">
                건
              </span>
            </span>
          </div>
          <div className="mt-1 flex items-baseline justify-between">
            {/* 2026-08-31: 단위를 '종'→'건'으로. 같은 물질이 여러 스캔에서
                나오면 각 스캔에 기록되므로 후보 행 수는 종 수가 아니다. */}
            <span className="text-sm text-gray-700">AI 후보</span>
            <span
              data-testid="candidate-count"
              className="text-base font-bold text-gray-900"
            >
              {candidateCount}
              <span className="ml-0.5 text-xs font-normal text-gray-500">
                건
              </span>
            </span>
          </div>
          <div className="mt-1 flex items-baseline justify-between">
            <span className="text-sm text-gray-700">물질(중복 제외)</span>
            <span
              data-testid="distinct-compound-count"
              className="text-base font-bold text-gray-900"
            >
              {distinctCompoundCount}
              <span className="ml-0.5 text-xs font-normal text-gray-500">
                종
              </span>
            </span>
          </div>
          {SHOW_JSD_LABEL && jsdScored.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-200">
              <div className="text-xs text-gray-500 mb-1">
                동정 신뢰도 분포{" "}
                <span className="text-[10px] text-gray-400">
                  (JSD 스펙트럼 유사도)
                </span>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span className="text-green-700">
                  신뢰 높음{" "}
                  <span className="font-bold font-mono">{jsdHigh}</span>
                </span>
                <span className="text-yellow-700">
                  확인 필요{" "}
                  <span className="font-bold font-mono">{jsdMid}</span>
                </span>
                <span className="text-red-700">
                  약함 <span className="font-bold font-mono">{jsdLow}</span>
                </span>
              </div>
            </div>
          )}
        </div>

        {/* 분석 상태 — 2026-08-28: 여기는 status 와 무관하게 "AI 분석 완료" 를
            하드코딩하고 있었다. 이 패널은 분석 상태와 무관하게 항상 렌더되므로
            (SampleAnalysisPage 의 우측 aside 에 조건이 없다) 실행 중·실패 분석과
            MS 결측 시료에서 화면이 없는 사실을 말했다. 실제 상태를 배선한다. */}
        {analysisStatus && (
          <div className="pt-3 border-t border-gray-200">
            <div className="text-sm font-medium text-gray-700 mb-2">
              분석 상태
            </div>
            <div className="flex items-center space-x-2">
              <div
                className={`w-3 h-3 rounded-full ${
                  STATUS_DOT[analysisStatus] ?? "bg-gray-400"
                }`}
              />
              <span className="text-sm text-gray-600">
                {STATUS_TEXT[analysisStatus] ?? analysisStatus}
              </span>
            </div>
            {/* 분석 행은 끝났어도 동정은 수행되지 않은 시료가 있다(D19). */}
            {isMsMissing && (
              <p className="mt-1.5 text-xs text-gray-500">
                화합물 동정 불가 — MS 미수집 (N/A, 비검출 아님)
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AIAnalysisSummary;
